import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { INFO_TERMS } from './infoTerms';
import './InfoTooltip.css';

const POP_WIDTH = 280;

/* Bouton ⓘ à poser à côté d'un titre/sigle métier — au clic, ouvre une petite
   pop-up avec une définition courte. Remplace `title=""` (natif HTML, muet au
   tactile, sans bouton visible) — décision 2026-08-13 (retours client sur
   LCR/CCR). Composant générique : le CONTENU vit dans `infoTerms.js`
   (dictionnaire FR par clé), pas ici — un seul composant, réutilisable pour
   tout sigle (LCR, CCR, temps TT→TR, BCR plus tard…).

   Rendue via un PORTAIL (document.body) : les cards de la charte ont toutes
   `overflow: hidden` (coins arrondis) — une pop-up positionnée normalement à
   l'intérieur se retrouvait tronquée (constat 2026-08-13, tuile LCR moyen).
   Position calculée en `fixed` à l'ouverture, indépendante de tout ancêtre.

   Props : term (clé de infoTerms.js) — ou title/body directement si
   l'appelant ne veut pas passer par le dictionnaire partagé. */
export default function InfoTooltip({ term, title, body, className = '', align = 'left' }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const def = term ? INFO_TERMS[term] : null;
  title = title ?? def?.title;
  body = body ?? def?.body;

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const left = align === 'right'
      ? Math.max(8, r.right - POP_WIDTH)
      : Math.min(r.left, window.innerWidth - POP_WIDTH - 8);
    setPos({ top: r.bottom + 6, left });
  };

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
    // eslint-disable-next-line
  }, [open]);

  if (!title && !body) return null;

  return (
    <span className={`info-tt ${className}`} ref={wrapRef}>
      <button
        type="button" ref={btnRef} className="info-tt__btn"
        aria-label={title || 'Information'}
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      >i</button>
      {open && pos && createPortal(
        <div
          className="info-tt__pop" role="dialog"
          style={{ top: pos.top, left: pos.left, width: POP_WIDTH }}
          onClick={(e) => e.stopPropagation()}
        >
          {title && <div className="info-tt__title">{title}</div>}
          {body && <div className="info-tt__body">{body}</div>}
        </div>,
        document.body,
      )}
    </span>
  );
}
